import {
  type DocumentMeta,
  type DocumentStatus,
  documentStatus,
  useSelectionManager,
} from "@prismicio/editor-fields"
import { range } from "@prismicio/editor-support/Array"
import { getDateDiffString } from "@prismicio/editor-support/Date"
import {
  Avatar,
  Badge,
  Checkbox,
  ErrorBoundary,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
} from "@prismicio/editor-ui"
import { type MouseEvent, Suspense } from "react"
import { Link } from "react-router-dom"

import {
  useAiABMLandingPagesFlag,
  useAiProductLandingPagesFlag,
} from "../../app/integrations/AmplitudeExperiments"
import { useCustomType } from "../../app/services/customTypes"
import { useBulkAction } from "../BulkActions/BulkActionsProvider"
import { isStandardTaskRecord } from "../BulkActions/bulkActionsService"
import type { Tab } from "../DocumentListHeader/DocumentListHeader"
import { useDocumentListSearchParams } from "../useDocumentListSearchParams"
import { DocumentListPageOptions } from "./DocumentListPageOptions"

interface DocumentListTableProps {
  documents: DocumentMeta[]
  tab: Tab
}

export function DocumentListTable(props: DocumentListTableProps) {
  const { documents, tab } = props

  const { isTransitioning } = useDocumentListSearchParams()

  const selectionManager = useSelectionManager({
    totalItemCount: documents.length,
  })

  function onSelectItem(id: string, event: MouseEvent | KeyboardEvent) {
    const ids = event.shiftKey ? documents.map(({ id }) => id) : undefined

    selectionManager.toggleSelection({
      id,
      metaKey: event.metaKey,
      shiftKey: event.shiftKey,
      ids,
    })
  }

  const aiABMLandingPagesEnabled =
    useAiABMLandingPagesFlag({
      active: tab === "working",
    }) === "on"

  const aiProductLandingPagesEnabled =
    useAiProductLandingPagesFlag({
      active: tab === "working",
    }) === "on"

  const isPageOptionsEnabled =
    aiABMLandingPagesEnabled || aiProductLandingPagesEnabled

  const columnLayout = getColumnLayout({ pageOptions: isPageOptionsEnabled })

  return (
    <Table isLoading={isTransitioning} columnLayout={columnLayout}>
      <DocumentListTableHeader pageOptions={isPageOptionsEnabled} />
      <TableBody>
        {documents.map((document) => (
          <DocumentListTableRow
            document={document}
            pageOptions={isPageOptionsEnabled}
            flags={{
              aiProductLandingPagesEnabled,
              aiABMLandingPagesEnabled,
            }}
            onSelectCheckbox={(_, event) => onSelectItem(document.id, event)}
            isChecked={selectionManager.isSelected(document.id)}
            // Add status to key because of an API bug where the document id can be returned twice PBD-1082
            key={`${document.id}-${document.versions[0]?.status ?? ""}`}
          />
        ))}
      </TableBody>
    </Table>
  )
}

interface DocumentListTableHeaderProps {
  pageOptions?: boolean
  checkbox?: boolean
}

export function DocumentListTableHeader(props: DocumentListTableHeaderProps) {
  const { pageOptions = false, checkbox = true } = props

  return (
    <TableHead>
      <TableRow>
        {checkbox && <TableCell />}
        <DocumentListTableHeaderCell text="Status" />
        <DocumentListTableHeaderCell text="Name" />
        <DocumentListTableHeaderCell text="Type" />
        <DocumentListTableHeaderCell text="Last update" />
        <DocumentListTableHeaderCell
          text="Author"
          align={!pageOptions ? "end" : undefined}
        />
        {pageOptions && <DocumentListTableHeaderCell text="" align="end" />}
      </TableRow>
    </TableHead>
  )
}

interface DocumentListTableHeaderCellProps {
  text: string
  align?: "end"
}

function DocumentListTableHeaderCell(props: DocumentListTableHeaderCellProps) {
  const { text, align } = props

  return (
    <TableCell align={align}>
      <Text variant="smallBold" color="grey11" noSelect noWrap>
        {text}
      </Text>
    </TableCell>
  )
}

interface DocumentListTableRowProps {
  document: DocumentMeta
  checkbox?: boolean
  isChecked?: boolean
  pageOptions?: boolean
  flags?: {
    aiProductLandingPagesEnabled: boolean
    aiABMLandingPagesEnabled: boolean
  }
  onSelectCheckbox?: (checked: boolean, event: MouseEvent) => void
}

export function DocumentListTableRow(props: DocumentListTableRowProps) {
  const {
    document,
    checkbox = true,
    isChecked = false,
    pageOptions = false,
    flags,
    onSelectCheckbox,
  } = props
  const { id, title, custom_type_id: customTypeId, versions } = document

  const { isTransitioning } = useDocumentListSearchParams()

  const lastEdited = versions[0]
    ? getDateDiffString(versions[0].last_modified_date)
    : ""

  const author = versions[0]?.author
  const status = versions[0]?.status ?? "unclassified"

  const { documentsInQueue } = useBulkAction()
  const inProgressInBulkAction = documentsInQueue.some((_document) =>
    isStandardTaskRecord(_document)
      ? _document.documentId === document.id
      : _document.documentGroupLangId === document.group_lang_id,
  )

  function onCheckedChange(checked: boolean, event: MouseEvent) {
    if (inProgressInBulkAction) return

    onSelectCheckbox?.(checked, event)
  }

  return (
    <TableRow
      disabled={inProgressInBulkAction}
      renderLink={(children) => (
        <Link to={`/pages/${id}?s=${status}`}>{children}</Link>
      )}
    >
      {checkbox && (
        <TableCell interactive={!isTransitioning}>
          <Checkbox
            checked={isChecked}
            tabIndex={-1}
            disabled={isTransitioning}
            onCheckedChange={onCheckedChange}
          />
        </TableCell>
      )}
      <TableCell>
        <StatusBadge status={status} />
      </TableCell>
      <TableCell>
        <Text variant="bold" color="grey12" noWrap>
          {title}
        </Text>
      </TableCell>
      <TableCell>
        <ErrorBoundary>
          <Suspense fallback={<Skeleton width={56} height={16} />}>
            <CustomTypeName id={customTypeId} />
          </Suspense>
        </ErrorBoundary>
      </TableCell>
      <TableCell>
        <Text variant="small" color="grey11" noWrap>
          {lastEdited}
        </Text>
      </TableCell>
      <TableCell align={!pageOptions ? "end" : undefined}>
        <Avatar
          firstName={author?.first_name}
          lastName={author?.last_name}
          imageUrl={author?.uploadedAvatar}
          border="grey2"
        />
      </TableCell>
      {pageOptions && (
        <TableCell align="end" interactive={!isTransitioning}>
          <DocumentListPageOptions
            document={document}
            options={{
              generateProductLandingPages:
                flags?.aiProductLandingPagesEnabled ?? false,
              personalizeLandingPageForABM:
                flags?.aiABMLandingPagesEnabled ?? false,
            }}
          />
        </TableCell>
      )}
    </TableRow>
  )
}

interface CustomTypeNameProps {
  id: string
}

function CustomTypeName(props: CustomTypeNameProps) {
  const { id } = props

  const customType = useCustomType(id)

  return (
    <Text variant="small" color="grey11" noWrap>
      {customType.label}
    </Text>
  )
}

interface StatusBadgeProps {
  status: DocumentStatus
}

function StatusBadge(props: StatusBadgeProps) {
  const { status } = props

  const { title, color } = documentStatus[status]

  return <Badge title={title} color={color} size="medium" maxWidth={150} />
}

interface DocumentListLoadingTableProps {
  checkbox?: boolean
  numberOfRows?: number
  border?: boolean
  radius?: boolean
  columns?: string
}

export function DocumentListLoadingTable(props: DocumentListLoadingTableProps) {
  const {
    checkbox = true,
    numberOfRows = 10,
    border = true,
    radius = true,
    columns = getColumnLayout({ pageOptions: false }),
  } = props

  return (
    <Table
      columnLayout={columns}
      isInteractive={false}
      border={border}
      radius={radius}
    >
      <DocumentListTableHeader checkbox={checkbox} />
      <TableBody>
        {range(0, numberOfRows - 1).map((num) => (
          <TableLoadingRow key={num} checkbox={checkbox} />
        ))}
      </TableBody>
    </Table>
  )
}

interface TableLoadingRowProps {
  checkbox?: boolean
}

function TableLoadingRow(props: TableLoadingRowProps) {
  const { checkbox = true } = props

  return (
    <TableRow>
      {checkbox && (
        <TableCell>
          <Checkbox tabIndex={-1} disabled />
        </TableCell>
      )}
      <TableCell>
        <Skeleton width={40} height={24} />
      </TableCell>
      <TableCell>
        <Skeleton width={371} height={16} />
      </TableCell>
      <TableCell>
        <Skeleton width={56} height={16} />
      </TableCell>
      <TableCell>
        <Skeleton width={56} height={16} />
      </TableCell>
      <TableCell align="end">
        <Skeleton width={32} height={32} borderRadius="100%" />
      </TableCell>
    </TableRow>
  )
}

const getColumnLayout = ({ pageOptions }: { pageOptions: boolean }) => `
  30px
  ${minWidthColumn("2fr")}
  ${minWidthColumn("13fr")}
  ${minWidthColumn("4fr")}
  ${minWidthColumn("3fr")}
  ${minWidthColumn("1fr")}
  ${pageOptions ? "40px" : ""}
`

export const getColumnLayoutWithoutCheckbox = ({
  pageOptions,
}: {
  pageOptions: boolean
}) => `
  ${minWidthColumn("2fr")}
  ${minWidthColumn("13fr")}
  ${minWidthColumn("4fr")}
  ${minWidthColumn("3fr")}
  ${minWidthColumn("1fr")}
  ${pageOptions ? "40px" : ""}
`

// For the more important content columns, this minimum width
// gives them a chance to look decent on smaller screens and/or show an ellipsis.
function minWidthColumn(max: string) {
  return `minmax(60px, ${max})`
}
